import { formatCount } from '@/lib/formatters';
import { iconFor, labelFor } from '@/lib/breakdownIcons';
import type { BreakdownDim, BreakdownItem } from '@/lib/types';

interface BreakdownListProps {
  data: BreakdownItem[];
  dim: BreakdownDim;
  topN?: number;
}

function withOther(data: BreakdownItem[], topN: number): BreakdownItem[] {
  if (data.length <= topN) return data;
  const head = data.slice(0, topN);
  const tail = data.slice(topN);
  const other = tail.reduce((sum, item) => sum + item.count, 0);
  return [...head, { key: '__other__', count: other }];
}

export function BreakdownList({ data, dim, topN = 8 }: BreakdownListProps) {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        No data in this range.
      </p>
    );
  }

  const rows = withOther(data, topN);
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0) || 1;
  const total = rows.reduce((s, r) => s + r.count, 0);

  return (
    <ul className="space-y-1.5">
      {rows.map((row) => {
        const isOther = row.key === '__other__';
        const label = isOther ? 'Other' : labelFor(dim, row.key);
        const icon = isOther ? null : iconFor(dim, row.key);
        const widthPct = (row.count / max) * 100;
        const sharePct = total === 0 ? 0 : (row.count / total) * 100;

        return (
          <li key={row.key} className="relative">
            <div
              className="absolute inset-y-0 left-0 rounded-md bg-primary/10"
              style={{ width: `${widthPct}%` }}
              aria-hidden
            />
            <div className="relative flex items-center justify-between gap-3 px-2 py-1.5 text-sm">
              <div className="flex min-w-0 items-center gap-2">
                {icon}
                <span className="truncate">{label}</span>
              </div>
              <div className="flex shrink-0 items-baseline gap-2 tabular-nums">
                <span className="text-xs text-muted-foreground">
                  {sharePct.toFixed(1)}%
                </span>
                <span className="font-medium">{formatCount(row.count)}</span>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
